import i18n from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useMountedState } from 'react-use'

import { graphql } from '~/gql'
import { type OutputType, type SegmentMode } from '~/gql/graphql'
import { useInvalidateBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { useNotify } from '~/hooks/useNotify'
import { toOutputLanguage } from '~/locales'
import { exists, isAiDisabledError } from '~/utils'

const generateCommentMutationDocument = graphql(`
  mutation AnalysisCommentBlock_GenerateAnalysisComments(
    $surveyId: String!
    $outputs: [AnalysisCommentInput!]!
    $outputLanguage: OutputLanguage!
    $origin: String
  ) {
    generateAnalysisComments(
      surveyId: $surveyId
      outputs: $outputs
      outputLanguage: $outputLanguage
      origin: $origin
    ) {
      awaitId
    }
  }
`)

const generateCommentStatusQueryDocument = graphql(`
  query AnalysisCommentBlock_GenerateAnalysisCommentsStatus($awaitId: String!) {
    generateAnalysisCommentsStatus(awaitId: $awaitId) {
      arrived
      error
      progress {
        completed
        total
      }
      result {
        comments {
          brandName
          comment
          filterPatternIndex
          outputType
          segmentMode
        }
      }
    }
  }
`)

type GenerateSingleCommentParams = {
  brandName: string
  filterDescription: string
  filterPatternIndex: number
  outputType: OutputType
  segmentMode: SegmentMode
  surveyId: string
  tabulationData: string
}

export const useGenerateSingleComment = () => {
  const isMounted = useMountedState()
  const graphQLRequest = useGraphQLRequest()
  const invalidateBrandAccountAiEnabledQuery =
    useInvalidateBrandAccountAiEnabledQuery()
  const notify = useNotify()
  const { t } = useTranslation()
  const [isGenerating, setIsGenerating] = React.useState<boolean>(false)
  const isGeneratingRef = React.useRef(false)

  // Contract: resolves to string | null and never rejects — all errors are caught internally.
  const generate = React.useCallback(
    async (params: GenerateSingleCommentParams): Promise<string | null> => {
      if (isGeneratingRef.current) {
        return null
      }
      isGeneratingRef.current = true

      const { surveyId, ...output } = params

      setIsGenerating(true)

      try {
        const mutationResult = await graphQLRequest({
          document: generateCommentMutationDocument,
          variables: {
            origin: 'figure',
            outputLanguage: toOutputLanguage(i18n.language),
            outputs: [output],
            surveyId,
          },
        })

        const awaitId =
          mutationResult.generateAnalysisComments?.awaitId ?? undefined
        if (!exists(awaitId)) {
          isGeneratingRef.current = false
          if (isMounted()) {
            setIsGenerating(false)
          }

          return null
        }

        return await new Promise<string | null>((resolve) => {
          const poll = async () => {
            if (!isMounted()) {
              isGeneratingRef.current = false
              resolve(null)

              return
            }

            try {
              const queryResult = await graphQLRequest({
                document: generateCommentStatusQueryDocument,
                variables: { awaitId },
              })

              const status = queryResult.generateAnalysisCommentsStatus

              if (!status?.arrived) {
                setTimeout(poll, 2000)

                return
              }

              if (exists(status.error)) {
                console.error('AI comment generation failed:', status.error)
                notify({ content: t('an_error_has_occurred'), style: 'error' })
                isGeneratingRef.current = false
                if (isMounted()) {
                  setIsGenerating(false)
                }
                resolve(null)

                return
              }

              const comment = status.result?.comments[0]?.comment ?? null

              isGeneratingRef.current = false
              if (isMounted()) {
                setIsGenerating(false)
              }

              resolve(comment)
            } catch (error) {
              console.error(
                'Polling for single analysis comment failed:',
                error,
              )
              notify({ content: t('an_error_has_occurred'), style: 'error' })
              isGeneratingRef.current = false
              if (isMounted()) {
                setIsGenerating(false)
              }
              resolve(null)
            }
          }

          poll()
        })
      } catch (error) {
        console.error('generateAnalysisComments mutation failed:', error)
        const aiDisabled = isAiDisabledError(error)

        if (aiDisabled) {
          // Reaching the server gate means the cached aiEnabled flag still said
          // "on". Drop it so the generate button turns disabled instead of
          // letting the next click hit the same rejection.
          void invalidateBrandAccountAiEnabledQuery()
        }

        // AI-off comes from a tenant setting the user can get lifted by asking
        // the right people, so it gets its own message rather than the generic one.
        notify({
          content: aiDisabled
            ? t('ai_disabled_message')
            : t('an_error_has_occurred'),
          style: 'error',
        })
        isGeneratingRef.current = false
        if (isMounted()) {
          setIsGenerating(false)
        }

        return null
      }
    },
    [
      graphQLRequest,
      invalidateBrandAccountAiEnabledQuery,
      isMounted,
      notify,
      t,
    ],
  )

  return { generate, isGenerating }
}
